import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { MediaRecording, MediaRecordingDiarizationIntervention } from 'src/app/models/media-recording.model';
import { User } from 'src/app/models/user.model';

export function resolveMediaRecordingDiarizationInterventions(media_recording: MediaRecording): MediaRecordingDiarizationIntervention[] {
  if (media_recording.diarization_interventions?.length) {
    return media_recording.diarization_interventions;
  }

  const diarization_json = media_recording.diarization_json;

  if (!diarization_json) {
    return [];
  }

  if (diarization_json.interventions?.length) {
    return diarization_json.interventions;
  }

  const speaker_assignments = new Map(
    (diarization_json.speaker_assignments ?? []).map(assignment => [assignment.speaker, assignment.user_id ?? null])
  );

  return (diarization_json.segments ?? []).map(segment => ({
    start_seconds: segment.start_seconds,
    end_seconds: segment.end_seconds,
    text: segment.text,
    speaker_label: segment.speaker,
    user_id: speaker_assignments.get(segment.speaker) ?? null,
    user: null,
  }));
}

interface YoutubePlayerInstance {
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  getCurrentTime(): number;
  getPlayerState(): number;
  destroy(): void;
}

interface YoutubePlayerConstructor {
  new (
    element: HTMLElement,
    options: {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: () => void;
        onError?: () => void;
        onStateChange?: (event: { data: number }) => void;
      };
    }
  ): YoutubePlayerInstance;
}

interface YoutubeNamespace {
  Player: YoutubePlayerConstructor;
  PlayerState: {
    PLAYING: number;
  };
}

declare global {
  interface Window {
    YT?: YoutubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

@Component({
  selector: 'app-media-recording-diarization-page',
  templateUrl: './media-recording-diarization-page.component.html',
  styleUrls: ['./media-recording-diarization-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
})
export class MediaRecordingDiarizationPageComponent implements OnInit, OnDestroy {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  @ViewChild('media_player') private media_player_ref?: ElementRef<HTMLAudioElement | HTMLVideoElement>;
  @ViewChild('youtube_player_container') private youtube_player_container_ref?: ElementRef<HTMLDivElement>;

  private activated_route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private api = inject(ApiService);
  private fb = inject(FormBuilder);
  private snack_bar = inject(MatSnackBar);
  private change_detector_ref = inject(ChangeDetectorRef);
  private destroy_ref = inject(DestroyRef);

  protected meeting_record_id = 0;
  protected media_recording_id = 0;
  protected recording_concept = '';
  protected assistant_users: User[] = [];
  protected can_edit = false;
  protected is_loading = true;
  protected is_saving = false;
  protected load_error: string | null = null;

  protected playback_object_url: string | null = null;
  protected is_loading_playback = false;
  protected playback_error: string | null = null;
  protected supports_local_playback = false;
  protected local_playback_kind: 'audio' | 'video' | null = null;
  protected supports_youtube_playback = false;
  protected youtube_video_id: string | null = null;
  protected is_loading_youtube = false;
  protected youtube_playback_error: string | null = null;
  protected is_audio_fallback_from_youtube = false;

  protected active_intervention_index = signal<number | null>(null);
  protected speaker_labels: string[] = [];

  protected readonly unassigned_user_option_value = 0;

  protected form: FormGroup = this.fb.group({
    interventions: this.fb.array<FormGroup>([]),
  });

  protected speaker_bulk_form: FormGroup = this.fb.group({});

  private last_scrolled_intervention_index: number | null = null;
  private youtube_player: YoutubePlayerInstance | null = null;
  private is_youtube_player_ready = false;
  private youtube_poll_timer: ReturnType<typeof setInterval> | null = null;
  private has_local_audio_fallback = false;
  private has_attempted_local_audio_fallback = false;

  ngOnInit(): void {
    this.meeting_record_id = Number(this.activated_route.snapshot.paramMap.get('meeting_record_id'));
    this.media_recording_id = Number(this.activated_route.snapshot.paramMap.get('media_recording_id'));
    this.can_edit = this.activated_route.snapshot.queryParamMap.get('can_edit') === '1';

    this.loadPageData();
  }

  ngOnDestroy(): void {
    this.destroyYoutubePlayer();
    this.revokePlaybackObjectUrl();
  }

  protected get interventions_form_array(): FormArray<FormGroup> {
    return this.form.get('interventions') as FormArray<FormGroup>;
  }

  protected formatInterventionTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remaining_seconds = Math.floor(seconds % 60);

    return `${String(minutes).padStart(2, '0')}:${String(remaining_seconds).padStart(2, '0')}`;
  }

  protected goBack(): void {
    const return_url = this.activated_route.snapshot.queryParamMap.get('return_url');

    if (return_url) {
      void this.router.navigateByUrl(return_url);
      return;
    }

    this.location.back();
  }

  protected onMediaTimeUpdate(): void {
    const media_player = this.media_player_ref?.nativeElement;

    if (!media_player) {
      return;
    }

    this.updateActiveInterventionForTime(media_player.currentTime);
  }

  protected seekToIntervention(index: number): void {
    const intervention_group = this.interventions_form_array.at(index);
    const start_seconds = Number(intervention_group.get('start_seconds')?.value ?? 0);
    const media_player = this.media_player_ref?.nativeElement;

    if (media_player) {
      media_player.currentTime = start_seconds;

      if (media_player.paused) {
        void media_player.play();
      }
    } else if (this.is_youtube_player_ready && this.youtube_player) {
      this.youtube_player.seekTo(start_seconds, true);
      this.youtube_player.playVideo();
    }

    this.active_intervention_index.set(index);
    this.scrollToIntervention(index);
  }

  private applySpeakerAssignment(speaker_label: string, bulk_user_id: number | null): void {
    for (const intervention_group of this.interventions_form_array.controls) {
      if (String(intervention_group.get('speaker_label')?.value ?? '') !== speaker_label) {
        continue;
      }

      intervention_group.patchValue({ user_id: bulk_user_id }, { emitEvent: false });
    }

    this.change_detector_ref.markForCheck();
  }

  protected save(): void {
    if (!this.can_edit || this.is_saving) {
      return;
    }

    const interventions = this.interventions_form_array.controls.map(control => {
      const raw_user_id = control.get('user_id')?.value as number | null;
      const user_id = raw_user_id === this.unassigned_user_option_value ? null : raw_user_id;
      const selected_user = this.assistant_users.find(user => user.id === user_id) ?? null;

      return {
        start_seconds: Number(control.get('start_seconds')?.value ?? 0),
        end_seconds: Number(control.get('end_seconds')?.value ?? 0),
        text: String(control.get('text')?.value ?? ''),
        speaker_label: String(control.get('speaker_label')?.value ?? ''),
        user_id,
        user: selected_user ? { id: selected_user.id, full_name: selected_user.full_name } : null,
      } satisfies MediaRecordingDiarizationIntervention;
    });

    this.is_saving = true;
    this.change_detector_ref.markForCheck();

    this.api
      .updateMediaRecordingDiarization(this.media_recording_id, interventions)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: () => {
          this.is_saving = false;
          this.snack_bar.open('Diarización guardada', undefined, { duration: 3000 });
          this.change_detector_ref.markForCheck();
        },
        error: () => {
          this.is_saving = false;
          this.snack_bar.open('No se pudo guardar la diarización', undefined, { duration: 4000 });
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private loadPageData(): void {
    this.is_loading = true;
    this.load_error = null;
    this.change_detector_ref.markForCheck();

    forkJoin({
      media_recording: this.api.getMediaRecording(this.media_recording_id),
      meeting_record: this.api.getMeetingRecord(String(this.meeting_record_id), { with: 'users' }),
    })
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: ({ media_recording, meeting_record }) => {
          this.recording_concept = media_recording.concept;
          this.assistant_users = meeting_record.users ?? [];
          this.buildInterventionForms(resolveMediaRecordingDiarizationInterventions(media_recording));
          this.initPlayback(media_recording);
          this.is_loading = false;
          this.change_detector_ref.markForCheck();
        },
        error: () => {
          this.is_loading = false;
          this.load_error = 'No se pudo cargar la diarización.';
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private buildInterventionForms(interventions: MediaRecordingDiarizationIntervention[]): void {
    this.interventions_form_array.clear();
    this.speaker_bulk_form = this.fb.group({});

    const speaker_labels_set = new Set<string>();

    for (const intervention of interventions) {
      speaker_labels_set.add(intervention.speaker_label);

      this.interventions_form_array.push(
        this.fb.group({
          start_seconds: [intervention.start_seconds],
          end_seconds: [intervention.end_seconds],
          text: [intervention.text],
          speaker_label: [intervention.speaker_label],
          user_id: [intervention.user_id ?? this.unassigned_user_option_value],
        })
      );
    }

    this.speaker_labels = [...speaker_labels_set].sort();

    for (const speaker_label of this.speaker_labels) {
      const assigned_user_id =
        interventions.find(
          intervention =>
            intervention.speaker_label === speaker_label && intervention.user_id !== null && intervention.user_id !== undefined
        )?.user_id ?? this.unassigned_user_option_value;

      const speaker_control = this.fb.control(assigned_user_id);

      speaker_control.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(user_id => {
        if (!this.can_edit) {
          return;
        }

        this.applySpeakerAssignment(speaker_label, user_id as number | null);
      });

      this.speaker_bulk_form.addControl(speaker_label, speaker_control);
    }
  }

  private initPlayback(media_recording: MediaRecording): void {
    this.has_local_audio_fallback = this.canFallbackToLocalAudio(media_recording);

    this.supports_youtube_playback =
      media_recording.media_type === 'video' && media_recording.youtube_video_id !== null && media_recording.youtube_video_id !== '';
    this.youtube_video_id = this.supports_youtube_playback ? media_recording.youtube_video_id : null;

    this.supports_local_playback = this.canStreamLocalMedia(media_recording);
    this.local_playback_kind = this.supports_local_playback ? this.resolveLocalPlaybackKind(media_recording) : null;

    if (this.supports_local_playback) {
      this.is_loading_playback = true;
      this.playback_error = null;
      this.change_detector_ref.markForCheck();

      this.api
        .streamMediaRecording(this.media_recording_id)
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe({
          next: blob => {
            this.revokePlaybackObjectUrl();
            this.playback_object_url = URL.createObjectURL(blob);
            this.is_loading_playback = false;
            this.change_detector_ref.markForCheck();
          },
          error: () => {
            this.is_loading_playback = false;
            this.playback_error = null;
            this.initYoutubePlaybackAsFallback();
            this.change_detector_ref.markForCheck();
          },
        });

      return;
    }

    this.initYoutubePlaybackAsFallback();
  }

  private initYoutubePlaybackAsFallback(): void {
    if (!this.supports_youtube_playback || this.youtube_video_id === null) {
      return;
    }

    this.is_loading_youtube = true;
    this.youtube_playback_error = null;
    this.change_detector_ref.markForCheck();

    setTimeout(() => {
      void this.setupYoutubePlayer();
    }, 0);
  }

  private async setupYoutubePlayer(): Promise<void> {
    if (!this.youtube_video_id) {
      return;
    }

    try {
      await this.loadYoutubeIframeApi();
    } catch {
      this.is_loading_youtube = false;
      this.initLocalAudioPlaybackFallback();
      return;
    }

    const container = this.youtube_player_container_ref?.nativeElement;

    if (!container || !window.YT?.Player) {
      this.is_loading_youtube = false;
      this.initLocalAudioPlaybackFallback();
      return;
    }

    this.destroyYoutubePlayer();

    this.youtube_player = new window.YT.Player(container, {
      videoId: this.youtube_video_id,
      width: '100%',
      height: '220',
      playerVars: {
        enablejsapi: 1,
        origin: window.location.origin,
      },
      events: {
        onReady: (): void => {
          this.is_loading_youtube = false;
          this.is_youtube_player_ready = true;
          this.change_detector_ref.markForCheck();
        },
        onError: (): void => {
          this.is_loading_youtube = false;
          this.initLocalAudioPlaybackFallback();
        },
        onStateChange: (event: { data: number }): void => {
          const is_playing = event.data === window.YT?.PlayerState.PLAYING;

          if (is_playing) {
            this.startYoutubeTimePolling();
            return;
          }

          this.stopYoutubeTimePolling();
        },
      },
    });
  }

  private loadYoutubeIframeApi(): Promise<void> {
    if (window.YT?.Player) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const resolve_when_ready = (): void => {
        if (window.YT?.Player) {
          resolve();
        }
      };

      const previous_ready_callback = window.onYouTubeIframeAPIReady;

      window.onYouTubeIframeAPIReady = (): void => {
        previous_ready_callback?.();
        resolve_when_ready();
      };

      const existing_script = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');

      if (!existing_script) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.onerror = () => reject(new Error('Failed to load YouTube IFrame API'));
        document.head.appendChild(script);
      }

      resolve_when_ready();
    });
  }

  private startYoutubeTimePolling(): void {
    this.stopYoutubeTimePolling();

    this.youtube_poll_timer = setInterval(() => {
      if (!this.youtube_player || !this.is_youtube_player_ready) {
        return;
      }

      this.updateActiveInterventionForTime(this.youtube_player.getCurrentTime());
    }, 250);
  }

  private stopYoutubeTimePolling(): void {
    if (this.youtube_poll_timer !== null) {
      clearInterval(this.youtube_poll_timer);
      this.youtube_poll_timer = null;
    }
  }

  private destroyYoutubePlayer(): void {
    this.stopYoutubeTimePolling();
    this.is_youtube_player_ready = false;

    if (this.youtube_player) {
      this.youtube_player.destroy();
      this.youtube_player = null;
    }
  }

  private canFallbackToLocalAudio(media_recording: MediaRecording): boolean {
    return media_recording.recording_status === 'completed' || media_recording.recording_status === 'upload_failed';
  }

  private initLocalAudioPlaybackFallback(): void {
    if (this.has_attempted_local_audio_fallback || !this.has_local_audio_fallback) {
      if (!this.has_local_audio_fallback) {
        this.youtube_playback_error = 'No se pudo reproducir el vídeo de YouTube.';
        this.playback_error = 'No hay archivo de audio disponible.';
        this.change_detector_ref.markForCheck();
      }

      return;
    }

    this.has_attempted_local_audio_fallback = true;
    this.destroyYoutubePlayer();
    this.supports_youtube_playback = false;
    this.youtube_video_id = null;
    this.youtube_playback_error = null;
    this.is_loading_youtube = false;
    this.supports_local_playback = true;
    this.local_playback_kind = 'audio';
    this.is_audio_fallback_from_youtube = true;
    this.is_loading_playback = true;
    this.playback_error = null;
    this.change_detector_ref.markForCheck();

    this.api
      .streamMediaRecordingAudio(this.media_recording_id)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: blob => {
          this.revokePlaybackObjectUrl();
          this.playback_object_url = URL.createObjectURL(blob);
          this.is_loading_playback = false;
          this.snack_bar.open('YouTube no disponible. Reproduciendo audio local.', undefined, { duration: 4000 });
          this.change_detector_ref.markForCheck();
        },
        error: () => {
          this.is_loading_playback = false;
          this.playback_error = 'No se pudo cargar el audio local.';
          this.change_detector_ref.markForCheck();
        },
      });
  }

  private updateActiveInterventionForTime(current_time: number): void {
    const active_index = this.findActiveInterventionIndex(current_time);

    if (this.active_intervention_index() === active_index) {
      return;
    }

    this.active_intervention_index.set(active_index);

    if (active_index !== null && active_index !== this.last_scrolled_intervention_index) {
      this.last_scrolled_intervention_index = active_index;
      this.scrollToIntervention(active_index);
    }
  }

  private findActiveInterventionIndex(current_time: number): number | null {
    for (let index = 0; index < this.interventions_form_array.length; index++) {
      const intervention_group = this.interventions_form_array.at(index);
      const start_seconds = Number(intervention_group.get('start_seconds')?.value ?? 0);
      const end_seconds = Number(intervention_group.get('end_seconds')?.value ?? 0);

      if (current_time >= start_seconds && current_time < end_seconds) {
        return index;
      }
    }

    return null;
  }

  private scrollToIntervention(index: number): void {
    const element = document.getElementById(`diarization-intervention-${index}`);

    element?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private canStreamLocalMedia(media_recording: MediaRecording): boolean {
    const has_playable_status = media_recording.recording_status === 'completed' || media_recording.recording_status === 'upload_failed';

    if (!has_playable_status || media_recording.storage_path === null) {
      return false;
    }

    if (this.resolveLocalPlaybackKind(media_recording) === null) {
      return false;
    }

    const has_youtube_playback =
      media_recording.media_type === 'video' && media_recording.youtube_video_id !== null && media_recording.youtube_video_id !== '';

    return !has_youtube_playback;
  }

  private resolveLocalPlaybackKind(media_recording: MediaRecording): 'audio' | 'video' | null {
    const mime_type = media_recording.mime_type ?? '';

    if (mime_type.startsWith('audio/') || media_recording.media_type === 'audio') {
      return 'audio';
    }

    const is_video_file = mime_type.startsWith('video/') || media_recording.storage_path?.endsWith('.mp4') === true;

    if (is_video_file) {
      return 'video';
    }

    if (media_recording.media_type === 'video' && media_recording.recording_status === 'upload_failed') {
      return 'video';
    }

    return null;
  }

  private revokePlaybackObjectUrl(): void {
    if (this.playback_object_url !== null) {
      URL.revokeObjectURL(this.playback_object_url);
      this.playback_object_url = null;
    }
  }
}
